import {
  Building2,
  FileText,
  Import,
  LayoutDashboard,
  Settings,
  User,
  Users,
  WalletCards,
} from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";

const Header = () => {
  const { darkModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  return (
    <>
      <nav className="grow border-b border-white/10 bg-[#152a2b] text-[#f9f4ec] shadow-[0_12px_28px_rgba(20,42,43,0.16)]">
        <header>
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6">
            <div className="flex min-h-[78px] items-center justify-between gap-4">
              <Link
                to="/"
                className="flex min-w-fit items-center gap-3 text-inherit no-underline"
              >
                <img
                  className="h-10 w-10 rounded-xl object-contain"
                  src={darkModeLogo}
                  alt={title}
                />
                <span className="grid gap-0.5 leading-none">
                  <span className="text-base font-semibold tracking-[-0.025em]">
                    {title}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#eeb59d]">
                    Relationship workspace
                  </span>
                </span>
              </Link>
              <div className="hidden min-w-0 flex-1 justify-center lg:flex">
                <nav className="flex items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.06] p-1.5">
                  <NavigationTab
                    icon={LayoutDashboard}
                    label={translate("ra.page.dashboard")}
                    to="/"
                    isActive={currentPath === "/"}
                  />
                  <NavigationTab
                    icon={Users}
                    label={translate("resources.contacts.name", {
                      smart_count: 2,
                    })}
                    to="/contacts"
                    isActive={currentPath === "/contacts"}
                  />
                  <NavigationTab
                    icon={Building2}
                    label={translate("resources.companies.name", {
                      smart_count: 2,
                    })}
                    to="/companies"
                    isActive={currentPath === "/companies"}
                  />
                  <NavigationTab
                    icon={WalletCards}
                    label={translate("resources.deals.name", {
                      smart_count: 2,
                    })}
                    to="/deals"
                    isActive={currentPath === "/deals"}
                  />
                </nav>
              </div>
              <div className="flex items-center rounded-xl border border-white/10 bg-black/10 px-1 text-[#f9f4ec]">
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <ImportFromJsonMenuItem />
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
            <nav className="flex gap-1 overflow-x-auto pb-3 lg:hidden">
              <NavigationTab
                icon={LayoutDashboard}
                label={translate("ra.page.dashboard")}
                to="/"
                isActive={currentPath === "/"}
              />
              <NavigationTab
                icon={Users}
                label={translate("resources.contacts.name", { smart_count: 2 })}
                to="/contacts"
                isActive={currentPath === "/contacts"}
              />
              <NavigationTab
                icon={Building2}
                label={translate("resources.companies.name", {
                  smart_count: 2,
                })}
                to="/companies"
                isActive={currentPath === "/companies"}
              />
              <NavigationTab
                icon={WalletCards}
                label={translate("resources.deals.name", { smart_count: 2 })}
                to="/deals"
                isActive={currentPath === "/deals"}
              />
            </nav>
          </div>
        </header>
      </nav>
    </>
  );
};

const NavigationTab = ({
  icon: Icon,
  label,
  to,
  isActive,
}: {
  icon: typeof LayoutDashboard;
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-all ${
      isActive
        ? "bg-[#f5ebdc] text-[#152a2b] shadow-sm"
        : "text-[#f9f4ec]/70 hover:bg-white/[0.08] hover:text-[#f9f4ec]"
    }`}
  >
    <Icon className="size-4" strokeWidth={1.8} />
    {label}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
